/**
 * Form for adding a new mobile money account
 */

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { MOBILE_NETWORKS, PHONE_REGEX } from '@/utils/constants'
import { convertToApiPhoneFormat } from '@/utils/format'

const newMobileMoneySchema = z.object({
  phone: z
    .string({ required_error: 'Phone number is required' })
    .regex(PHONE_REGEX, 'Please enter a valid 10-digit phone number (e.g., 0244123456)'),
  network: z.enum(['MTN', 'TELECEL', 'AIRTELTIGO'], {
    required_error: 'Please select a mobile network',
  }),
})

type NewMobileMoneyFormData = z.infer<typeof newMobileMoneySchema>

interface NewMobileMoneyFormProps {
  onSubmit: (data: { operator: string; mobile_number: string }) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

export function NewMobileMoneyForm({ onSubmit, onCancel, isLoading }: NewMobileMoneyFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewMobileMoneyFormData>({
    resolver: zodResolver(newMobileMoneySchema),
    defaultValues: {
      phone: '',
      network: 'MTN',
    },
  })

  const handleFormSubmit = async (data: NewMobileMoneyFormData) => {
    try {
      const mobileNumber = convertToApiPhoneFormat(data.phone)
      const payload = {
        operator: data.network,
        mobile_number: mobileNumber,
      }
      console.log('[NewMobileMoneyForm] Form submitted')
      console.log('[NewMobileMoneyForm] Form data:', data)
      console.log('[NewMobileMoneyForm] Payload:', payload)
      console.log('[NewMobileMoneyForm] Calling onSubmit prop...')

      await onSubmit(payload)

      console.log('[NewMobileMoneyForm] onSubmit completed successfully')
    } catch (error) {
      console.error('[NewMobileMoneyForm] Error in handleFormSubmit:', error)
      throw error // Re-throw to let form handle it
    }
  }

  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Add New Mobile Money Number</h3>
      
      <div>
        <label htmlFor="new-phone" className="block text-sm font-medium text-gray-700 mb-2">
          Mobile Number
        </label>
        <input
          id="new-phone"
          type="tel"
          {...register('phone')}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
            errors.phone ? 'border-error-500' : 'border-gray-300'
          }`}
          placeholder="0244123456"
          maxLength={10}
        />
        {errors.phone && <p className="mt-1 text-sm text-error-600">{errors.phone.message}</p>}
      </div>

      <div>
        <label htmlFor="new-network" className="block text-sm font-medium text-gray-700 mb-2">
          Mobile Network
        </label>
        <select
          id="new-network"
          {...register('network')}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
            errors.network ? 'border-error-500' : 'border-gray-300'
          }`}
        >
          {MOBILE_NETWORKS.map((network) => (
            <option key={network.value} value={network.value}>
              {network.label}
            </option>
          ))}
        </select>
        {errors.network && <p className="mt-1 text-sm text-error-600">{errors.network.message}</p>}
      </div>

      <div className="flex space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleSubmit(handleFormSubmit)()
          }}
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Adding...' : 'Add'}
        </button>
      </div>
    </div>
  )
}

